import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeSlash, ShieldLock, CheckLg, XLg } from "react-bootstrap-icons";

/* small API helper */
async function postJson(url, body, { signal } = {}) {
  const token = localStorage.getItem("authToken");
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { message: text };
  }
  if (!res.ok) {
    const err = new Error(data?.message || res.statusText || "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export default function ChangePassword() {
  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const abortRef = useRef(null);
  const resendTimerRef = useRef(null);

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [ui, setUi] = useState({
    showCurrent: false,
    showNew: false,
    showConfirm: false,
    loading: false,
    error: "",
    success: "",
    showMfa: false,
    mfaCode: "",
    attempts: 0,
    resendCooldown: 0,
  });

  const MAX_ATTEMPTS = 5;
  const RESEND_BASE = 30;

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      clearInterval(resendTimerRef.current);
    };
  }, []);

  // password rules
  const rules = {
    length: form.newPassword.length >= 8,
    uppercase: /[A-Z]/.test(form.newPassword),
    number: /[0-9]/.test(form.newPassword),
    special: /[^A-Za-z0-9]/.test(form.newPassword),
  };
  const score = Object.values(rules).filter(Boolean).length;
  const percent = (score / 4) * 100;
  const strengthClass =
    score <= 1 ? "bg-danger" : score === 2 ? "bg-warning" : "bg-success";

  // form validity used to disable Continue button
  const allFieldsFilled =
    form.currentPassword.trim() !== "" &&
    form.newPassword.trim() !== "" &&
    form.confirmPassword.trim() !== "";

  const meetsRules =
    rules.length && rules.uppercase && rules.number && rules.special;
  const passwordsMatch = form.newPassword === form.confirmPassword;
  const newIsDifferent = form.currentPassword !== form.newPassword;

  const isFormValid =
    allFieldsFilled &&
    meetsRules &&
    passwordsMatch &&
    newIsDifferent &&
    !ui.loading;

  const validate = useCallback(() => {
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword)
      return "All fields are required";
    if (!meetsRules)
      return "Password must be at least 8 characters and include uppercase, number, and special character";
    if (!passwordsMatch) return "Passwords do not match";
    if (!newIsDifferent) return "New password must be different";
    return null;
  }, [form, meetsRules, passwordsMatch, newIsDifferent]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    setUi((u) => ({ ...u, error: "", success: "" }));
  }, []);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setUi((u) => ({ ...u, error: "", success: "" }));

      // defensive check: ensure form is valid before proceeding
      if (!isFormValid) {
        const err = validate();
        return setUi((u) => ({
          ...u,
          error:
            err || "Please meet all password requirements before continuing.",
        }));
      }

      setUi((u) => ({ ...u, showMfa: true }));
    },
    [isFormValid, validate],
  );

  const confirmChange = useCallback(async () => {
    if (ui.attempts >= MAX_ATTEMPTS) {
      return setUi((u) => ({
        ...u,
        error: "Too many attempts. Try again later.",
      }));
    }
    if (!ui.mfaCode) return setUi((u) => ({ ...u, error: "Enter MFA code" }));

    abortRef.current = new AbortController();
    setUi((u) => ({ ...u, loading: true, error: "" }));
    try {
      await postJson(
        `${USER_API}/sellers/change-password`,
        {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          mfaCode: ui.mfaCode,
        },
        { signal: abortRef.current.signal },
      );

      setUi((u) => ({
        ...u,
        success: "Password changed. Redirecting...",
        showMfa: false,
      }));
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const attempts = Math.min(MAX_ATTEMPTS, ui.attempts + 1);
      setUi((u) => ({
        ...u,
        attempts,
        error: err.data?.message || err.message || "Verification failed",
      }));
    } finally {
      setUi((u) => ({ ...u, loading: false }));
    }
  }, [form, ui, navigate]);

  const resendCode = useCallback(async () => {
    if (ui.resendCooldown > 0) return;
    try {
      setUi((u) => ({ ...u, resendCooldown: RESEND_BASE, error: "" }));
      await postJson(`${USER_API}/sellers/resend-mfa`, {
        reason: "change-password",
      });

      resendTimerRef.current = setInterval(() => {
        setUi((u) => {
          if (u.resendCooldown <= 1) {
            clearInterval(resendTimerRef.current);
            return { ...u, resendCooldown: 0 };
          }
          return { ...u, resendCooldown: u.resendCooldown - 1 };
        });
      }, 1000);
    } catch (err) {
      setUi((u) => ({
        ...u,
        error: err.data?.message || "Failed to resend code",
      }));
    }
  }, [ui.resendCooldown]);

  const Rule = ({ valid, text }) => (
    <li
      className={
        valid
          ? "text-success d-flex align-items-center gap-2"
          : "text-danger d-flex align-items-center gap-2"
      }
    >
      {valid ? <CheckLg className="me-1" /> : <XLg className="me-1" />}{" "}
      <span>{text}</span>
    </li>
  );

  return (
    <div className="container mt-4" style={{ maxWidth: 520 }}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h4 className="mb-3">Change Password</h4>

          {ui.error && (
            <div role="alert" className="alert alert-danger">
              {ui.error}
            </div>
          )}
          {ui.success && (
            <div role="status" className="alert alert-success">
              {ui.success}
            </div>
          )}

          <form onSubmit={onSubmit} aria-describedby="password-requirements">
            <div className="mb-3">
              <label className="form-label">Current Password</label>
              <div className="input-group">
                <input
                  name="currentPassword"
                  type={ui.showCurrent ? "text" : "password"}
                  className="form-control"
                  value={form.currentPassword}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  aria-label="Current password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  aria-pressed={ui.showCurrent}
                  aria-label={
                    ui.showCurrent
                      ? "Hide current password"
                      : "Show current password"
                  }
                  title={
                    ui.showCurrent
                      ? "Hide current password"
                      : "Show current password"
                  }
                  onClick={() =>
                    setUi((u) => ({ ...u, showCurrent: !u.showCurrent }))
                  }
                >
                  {ui.showCurrent ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">New Password</label>
              <div className="input-group">
                <input
                  name="newPassword"
                  type={ui.showNew ? "text" : "password"}
                  className="form-control"
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                  aria-describedby="password-requirements"
                  aria-label="New password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  aria-pressed={ui.showNew}
                  aria-label={
                    ui.showNew ? "Hide new password" : "Show new password"
                  }
                  title={ui.showNew ? "Hide new password" : "Show new password"}
                  onClick={() => setUi((u) => ({ ...u, showNew: !u.showNew }))}
                >
                  {ui.showNew ? <EyeSlash /> : <Eye />}
                </button>
              </div>

              {form.newPassword && (
                <>
                  <div className="mt-2">
                    <div className="progress" style={{ height: 8 }}>
                      <div
                        className={`progress-bar ${strengthClass}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>

                  <ul
                    id="password-requirements"
                    className="mt-2 small mb-0 list-unstyled"
                  >
                    <Rule valid={rules.length} text="8+ characters" />
                    <Rule valid={rules.uppercase} text="Uppercase letter" />
                    <Rule valid={rules.number} text="Number" />
                    <Rule valid={rules.special} text="Special character" />
                  </ul>
                </>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Confirm Password</label>
              <div className="input-group">
                <input
                  name="confirmPassword"
                  type={ui.showConfirm ? "text" : "password"}
                  className="form-control"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  aria-label="Confirm password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  aria-pressed={ui.showConfirm}
                  aria-label={
                    ui.showConfirm
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                  title={
                    ui.showConfirm
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                  onClick={() =>
                    setUi((u) => ({ ...u, showConfirm: !u.showConfirm }))
                  }
                >
                  {ui.showConfirm ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <button
              className="btn w-100"
              type="submit"
              disabled={!isFormValid}
              aria-disabled={!isFormValid}
              aria-describedby={
                !isFormValid ? "password-requirements" : undefined
              }
              style={{
                backgroundColor: "#0b3d91",
                borderColor: "#0b3d91",
                color: "#fff",
              }}
            >
              {ui.loading ? "Processing…" : "Continue"}
            </button>

            {!isFormValid && (
              <div className="small text-muted mt-2" role="status">
                <span className="text-danger">*</span>Complete all requirements
                above and make sure you use the same password on new password
                and confirm password to enable Continue.
              </div>
            )}
          </form>
        </div>
      </div>

      {ui.showMfa && (
        <div
          className="modal show d-block"
          role="dialog"
          aria-modal="true"
          aria-label="MFA verification"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <ShieldLock className="me-2" />
                  MFA Verification
                </h5>
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setUi((u) => ({ ...u, showMfa: false }))}
                />
              </div>

              <div className="modal-body">
                <p className="small text-muted">
                  A verification code was sent to your registered device
                </p>

                <input
                  className="form-control mb-2"
                  placeholder="Enter code"
                  value={ui.mfaCode}
                  onChange={(e) =>
                    setUi((u) => ({ ...u, mfaCode: e.target.value, error: "" }))
                  }
                  inputMode="numeric"
                  aria-label="MFA code"
                />

                {ui.attempts > 0 && (
                  <div className="text-danger small">
                    Attempts: {ui.attempts}/{MAX_ATTEMPTS}
                  </div>
                )}

                <button
                  className="btn btn-link p-0 mt-2"
                  disabled={ui.resendCooldown > 0}
                  onClick={resendCode}
                  aria-disabled={ui.resendCooldown > 0}
                >
                  {ui.resendCooldown > 0
                    ? `Resend in ${ui.resendCooldown}s`
                    : "Resend Code"}
                </button>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setUi((u) => ({ ...u, showMfa: false }))}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={confirmChange}
                  disabled={ui.loading}
                >
                  {ui.loading ? "Verifying…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
